import EmberService, { service } from '@ember/service';

import type { APIData, PopupData, PopupListItem, RequestError, Service } from 'webapp-types';
import {
	ActivityLogEvent,
	ActivityLogFetchType,
	ActivityLogObjectType,
} from 'webapp/enums/activity-log';
import { BillingAddonType, BillingFeature } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';

import AccountLogsPopupComponent from 'webapp/components/account/logs/popup';

import type { ActivityLogObject } from 'webapp/models-custom/activity-log';
import ActivityLog from 'webapp/models-custom/activity-log';

type NumericQueryParam = number | number[];
export interface ActivityLogsQueryParams {
	pageSize?: number;
	page?: number;
	after?: number; // timestamp for filtering records
	before?: number; // timestamp for filtering records
	userId?: number;
	ensureUnique?: boolean;
	includeMessages?: boolean;
	eventIds?: NumericQueryParam;
	objectId?: number;
	objectTypes?: ActivityLogObjectType[];
	searchString?: string;
	groupForView?: string;
	fetchType?: ActivityLogFetchType;
	spaceId?: number;
}

export interface ActivityLogAccountQueryParams {
	fetchType: ActivityLogFetchType;
	spaceId?: number;
}

type ObjectTypeLists = Partial<Record<ActivityLogObjectType, number[]>>;

export default class ModelManagerActivityLogsService extends EmberService {
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service declare store: Service.Store;
	@service('account') declare accountService: Service.Account;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	// ------ Requests ------
	getActivityLogs(
		queryParams: ActivityLogsQueryParams,
	): Promise<APIData.Settings.ActivityLogResponse> {
		return this.requestService
			.api<APIData.Settings.ActivityLogResponse>('GET', '/activity-logs', queryParams)
			.then((reply) => {
				// reply = window.dbx_mock['GET /d/activity-logs'];

				return this.fetchObjectPrerequisites(reply).then(() => {
					return Promise.resolve(reply);
				});
			});
	}

	getActivityLogUsers(
		queryParams: ActivityLogAccountQueryParams,
	): Promise<APIData.Settings.ActivityLogUsersResponse> {
		return this.requestService.api<APIData.Settings.ActivityLogUsersResponse>(
			'GET',
			'/activity-logs/users',
			queryParams,
		);
	}

	fetchObjectPrerequisites(response: APIData.Settings.ActivityLogResponse) {
		const objectTypeLists: ObjectTypeLists = {},
			currentSpaceId = this.accountService.id;

		const currentSpaceLogs = response.activityLogs.filter(
			(log) => !log.spaceId || log.spaceId === currentSpaceId,
		);

		currentSpaceLogs.reduce((otl: ObjectTypeLists, log: ActivityLogObject) => {
			const logObjectType = log.objectType;
			if (!otl[logObjectType]) {
				otl[logObjectType] = [];
			}
			otl[logObjectType]?.push(log.objectId);

			return otl;
		}, objectTypeLists);

		return this.fetchObjectReferenceData(objectTypeLists); // Note: allSettled doesn't fail on failed promises
	}

	private fetchObjectReferenceData(objectTypeLists: ObjectTypeLists) {
		const promises: Record<string, Promise<unknown>> = {};

		Object.keys(objectTypeLists).forEach((objectTypeListKey) => {
			const objectTypeListKeyNumber: ActivityLogObjectType = Number(objectTypeListKey),
				objectIds = objectTypeLists[objectTypeListKeyNumber] || [];

			if (objectIds.length) {
				switch (objectTypeListKeyNumber) {
					case ActivityLogObjectType.BOARD:
						// No need to set promises.boards because all boards are already fetched as part of initialRequestsPromise
						break;
					case ActivityLogObjectType.BOARD_LOOP: {
						const objectIdsToRequest = objectIds.filter((objectId) => {
							return !this.mmBoardLoopsService.findRecord(String(objectId));
						});
						if (objectIdsToRequest.length) {
							promises.boardLoops =
								this.mmBoardLoopsService.requestActivityLogBoardLoops({
									queryParams: { boardLoopIds: objectIdsToRequest },
								});
						}
						break;
					}
					case ActivityLogObjectType.REPORT: {
						const objectIdsToRequest = objectIds.filter((objectId) => {
							return !this.mmReportsService.findRecord(String(objectId));
						});
						if (objectIdsToRequest.length) {
							promises.reports = this.mmReportsService.requestActivityLogReports({
								queryParams: { reportIds: objectIdsToRequest },
							});
						}
						break;
					}
					default:
						break;
				}
			}
		});

		return Promise.allSettled(Object.values(promises)).then((promiseResults) => {
			promiseResults.forEach((promiseResult) => {
				if (promiseResult.status === 'fulfilled' && !!promiseResult.value) {
					this.store.pushPayload(promiseResult.value);
				}
			});
		});
	}

	createAndSaveActivityLogItem(eventId: ActivityLogEvent, objectId: string) {
		const payload = {
			activityLog: {
				objectId: Number(objectId),
				eventId,
			},
		};

		void this.requestService.api('POST', '/activity-logs', payload);

		const eventIdsToTriggerRecentChange = [
			ActivityLogEvent.BOARD_VIEW,
			ActivityLogEvent.BOARD_LOOP_VIEW,
			ActivityLogEvent.REPORT_VIEW,
		];
		if (eventIdsToTriggerRecentChange.includes(eventId)) {
			this.eventManagerService.trigger(AppEvent.RECENT_ITEMS_VIEWED_CHANGED);
		}
	}

	createAndOpenPopup(props: PopupData.ActivityLog.Preview) {
		// A metric registry row's log serves a Demo user generated demo events, so only the other logs are held back.
		if (!('metricRegistryItem' in props) && this.accountService.handleDemo()) {
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.ACTIVITY_LOG, true)) {
			return null;
		}

		const popup = this.popupService.createModal(AccountLogsPopupComponent, {
			popupClass: 'popup-type-activity-log',
		});

		popup.open(props);

		return popup;
	}

	getObjectForOptionsPopup<Action>(): PopupListItem<Action> {
		return {
			text: 'Activity log',
			action: 'openActivityLog' as Action,
			icon: 'ActivityLog',
			extra: this.billingService.activityLogEnabled
				? undefined
				: {
						type: 'featureLimitation',
					},
		};
	}

	enableAddon() {
		const model = this.billingService.getAddonInfo(BillingAddonType.ACTIVITY_LOG);

		if (model) {
			if (this.billingService.isTrial) {
				return this.billingService.addAddon(model.id).then(
					() => {
						this.infolayerService.ok('Addon added!');
					},
					(errorData: RequestError) => {
						this.requestService.handleGenericThrownError(errorData);
					},
				);
			} else {
				this.billingService.openAddonPurchasePopup(model);
			}
		}

		return Promise.resolve();
	}

	// ---- Recents (boards, loops, reports) ----

	private getRecentlyViewedItemsPendingPromise: Promise<void | APIData.Settings.ActivityLogResponse> | null =
		null;

	getRecentlyViewedItems() {
		const pendingPromise = this.getRecentlyViewedItemsPendingPromise;
		if (pendingPromise) {
			return pendingPromise;
		} else {
			const userId = this.currentUserService.numericId,
				queryParams: ActivityLogsQueryParams = {
					pageSize: 10,
					eventIds: [
						ActivityLogEvent.BOARD_VIEW,
						ActivityLogEvent.BOARD_LOOP_VIEW,
						ActivityLogEvent.REPORT_VIEW,
					],
				};

			if (userId) {
				queryParams.userId = userId;
			}

			const promise = this.getActivityLogs(queryParams)
				.catch(() => {
					this.infolayerService.error('Could not load recently viewed items!');
				})
				.finally(() => {
					this.getRecentlyViewedItemsPendingPromise = null;
				});

			this.getRecentlyViewedItemsPendingPromise = promise;
			return promise;
		}
	}

	// ------ For-fake-store ------
	modelName = 'activity-log';

	createModel(model: ActivityLogObject): ActivityLog {
		return new ActivityLog(model);
	}

	createModelsFromRecordsArray(recordsArray: ActivityLogObject[]): ActivityLog[] {
		return recordsArray.map((properties: ActivityLogObject) => {
			return this.createModel(properties);
		});
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/activity-logs': ModelManagerActivityLogsService;
	}
}
